import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Modal, Input, Button, Spin, Progress } from "antd";
import { swalError, swalSuccess } from "../../utils/swal";
import { uploadToS3 } from "../N";
import "./HiggsDiscover.css";

const CATEGORY_COLORS = {
  "Image Generation": "#6F47E0",
  "Video Generation": "#47C4E0",
};

function HiggsDiscover({ visible, onClose, onAddAsset }) {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedModel, setSelectedModel] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [genStatus, setGenStatus] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (visible) {
      loadModels();
    }
  }, [visible]);

  const loadModels = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/higgsfield/models`
      );
      setModels(res.data);
    } catch (e) {
      console.error("Failed to load models", e);
    }
    setLoading(false);
  };

  const isVideoModel = selectedModel?.type === "image-to-video";

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImagePreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const url = await uploadToS3([{ file }]);
      setImageUrl(url);
    } catch (err) {
      swalError("Failed to upload image");
      setImagePreview(null);
    }
    setUploading(false);
    e.target.value = "";
  };

  const clearImage = () => {
    setImageUrl("");
    setImagePreview(null);
  };

  const handleGenerate = async () => {
    if (!prompt.trim() || !selectedModel) return;
    if (isVideoModel && !imageUrl) {
      swalError("Please upload an image for video generation");
      return;
    }
    setGenerating(true);
    setProgress(0);
    setGenStatus("Submitting...");

    try {
      const body = {
        prompt: prompt.trim(),
        modelId: selectedModel.id,
        modelType: selectedModel.type,
      };
      if (isVideoModel) {
        body.imageUrl = imageUrl.trim();
      }

      // Submit request
      const submitRes = await axios.post(
        `${process.env.REACT_APP_API_URL}/higgsfield/submit`,
        body
      );
      const requestId = submitRes.data?.requestId;
      if (!requestId) throw new Error("Failed to submit request");

      setProgress(10);
      setGenStatus("Generating...");

      // Poll for status
      const maxTime = 180000;
      const pollInterval = 3000;
      const startTime = Date.now();

      while (Date.now() - startTime < maxTime) {
        await new Promise((r) => setTimeout(r, pollInterval));

        const statusRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/higgsfield/status/${requestId}`
        );
        const { status, url, error } = statusRes.data;

        // Update progress based on elapsed time (10-90 range)
        const elapsed = Date.now() - startTime;
        const timeProgress = Math.min(90, 10 + (elapsed / maxTime) * 80);
        setProgress(Math.round(timeProgress));

        if (status === "in_progress") {
          setGenStatus("Processing...");
        }

        if (status === "completed" && url) {
          setProgress(100);
          setGenStatus("Done!");
          await onAddAsset(url, isVideoModel ? "video" : "image");
          swalSuccess("Asset generated and added to your board");
          setPrompt("");
          clearImage();
          setSelectedModel(null);
          setGenerating(false);
          setProgress(0);
          setGenStatus("");
          return;
        }

        if (status === "failed") {
          throw new Error(error || "Generation failed");
        }
      }

      throw new Error("Generation timed out");
    } catch (e) {
      const data = e.response?.data;
      const msg =
        (typeof data === "string" && data) ||
        data?.message ||
        data?.error ||
        data?.detail ||
        e.message ||
        "Generation failed. Please try again.";
      swalError(typeof msg === "string" ? msg : "Generation failed. Please try again.");
    }
    setGenerating(false);
    setProgress(0);
    setGenStatus("");
  };

  const filteredModels =
    filter === "all"
      ? models
      : models.filter((m) => m.category === filter);

  const categories = [...new Set(models.map((m) => m.category))];

  return (
    <Modal
      wrapClassName="ai-modal"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={860}
      title={null}
      destroyOnClose
    >
      <div className="higgs-discover">
        <div className="higgs-header">
          <h2 className="higgs-title">AI Models</h2>
          <p className="higgs-subtitle">
            Generate images and videos with cutting-edge AI models
          </p>
        </div>

        <div className="higgs-filters">
          <button
            className={`higgs-filter-btn ${filter === "all" ? "higgs-filter-active" : ""}`}
            onClick={() => setFilter("all")}
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              className={`higgs-filter-btn ${filter === cat ? "higgs-filter-active" : ""}`}
              onClick={() => setFilter(cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="higgs-loading">
            <Spin />
          </div>
        ) : (
          <div className="higgs-grid">
            {filteredModels.map((model) => (
              <div
                key={model.id}
                className={`higgs-model-card ${selectedModel?.id === model.id ? "higgs-model-card-selected" : ""}`}
                onClick={() => setSelectedModel(model)}
              >
                <div
                  className="higgs-model-badge"
                  style={{
                    background: CATEGORY_COLORS[model.category] || "#555",
                  }}
                >
                  {model.type === "text-to-image" ? "IMG" : "VID"}
                </div>
                <h4 className="higgs-model-name">{model.name}</h4>
                <span className="higgs-model-provider">{model.provider}</span>
                <p className="higgs-model-desc">{model.description}</p>
              </div>
            ))}
          </div>
        )}

        {selectedModel && (
          <div className="higgs-generate-bar">
            <div className="higgs-generate-info">
              <span className="higgs-generate-model-name">
                {selectedModel.name}
              </span>
              <span className="higgs-generate-model-type">
                {selectedModel.type === "text-to-image"
                  ? "Text to Image"
                  : "Image to Video"}
              </span>
            </div>
            {isVideoModel && (
              <div className="higgs-image-upload-row">
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleImageUpload}
                />
                {imagePreview || imageUrl ? (
                  <div className="higgs-image-preview">
                    <img
                      src={imagePreview || imageUrl}
                      alt="Source"
                      className="higgs-image-preview-img"
                    />
                    <div className="higgs-image-preview-actions">
                      <button
                        className="higgs-image-preview-change"
                        onClick={() => fileInputRef.current.click()}
                      >
                        Change
                      </button>
                      <button
                        className="higgs-image-preview-remove"
                        onClick={clearImage}
                      >
                        Remove
                      </button>
                    </div>
                    {uploading && (
                      <div className="higgs-image-uploading">
                        <Spin size="small" /> Uploading...
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    className="higgs-image-dropzone"
                    onClick={() => fileInputRef.current.click()}
                  >
                    <span className="higgs-image-dropzone-text">
                      Upload an image to animate
                    </span>
                    <span className="higgs-image-dropzone-hint">
                      Click to browse or drag and drop
                    </span>
                  </div>
                )}
              </div>
            )}
            <div className="higgs-generate-input-row">
              <Input.TextArea
                className="higgs-generate-input"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={
                  isVideoModel
                    ? "Describe the motion and camera movement..."
                    : "Describe the image you want to create..."
                }
                autoSize={{ minRows: 1, maxRows: 3 }}
                onPressEnter={(e) => {
                  if (!e.shiftKey) {
                    e.preventDefault();
                    handleGenerate();
                  }
                }}
              />
              <Button
                className="higgs-generate-btn"
                onClick={handleGenerate}
                loading={generating}
                disabled={!prompt.trim() || generating}
              >
                {generating ? "Generating..." : "Generate"}
              </Button>
            </div>
            {generating && (
              <div className="higgs-progress-section">
                <Progress
                  percent={progress}
                  strokeColor="#6F47E0"
                  trailColor="rgba(255,255,255,0.06)"
                  showInfo={false}
                  size="small"
                />
                <div className="higgs-progress-info">
                  <span className="higgs-progress-status">{genStatus}</span>
                  <span className="higgs-progress-percent">{progress}%</span>
                </div>
                <p className="higgs-generating-hint">This may take a few minutes. Please don't close this window.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

export default HiggsDiscover;
